import { FC, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ReactComponent as FacebookIcon } from 'src/assets/svg/facebook.svg'
import { ReactComponent as InstagramIcon } from 'src/assets/svg/instagram.svg'
import { ReactComponent as LinkedinIcon } from 'src/assets/svg/linkedin.svg'
import { ReactComponent as TiktokIcon } from 'src/assets/svg/tiktok.svg'
import { ReactComponent as YoutubeIcon } from 'src/assets/svg/youtube.svg'
import { ReactComponent as DeleteIcon } from 'src/assets/svg/delete.svg'
import { useDisableScroll } from 'src/hooks/useDisableScroll'
import { CampaignCategoryPicker } from 'src/components/CampaignCategoryPicker'
import classes from './analyzeRecentPostsModal.module.scss'

interface UrlEntry {
  id: string
  link: string
  platform: string
}

interface AnalyzeRecentPostsModalProps {
  initialData?: {
    analysisTitle: string
    postCount: number
    urls: UrlEntry[]
  } | null
  isOpen: boolean
  onClearAllLinks?: () => void
  onClose: () => void
  onNext?: (data: {
    analysisTitle: string
    category: string
    postCount: number
    urls: UrlEntry[]
  }) => void
}

const detectPlatformFromUrl = (url: string): string => {
  try {
    const urlWithProtocol = url.startsWith('http') ? url : `https://${url}`
    const { hostname } = new URL(urlWithProtocol)
    const host = hostname.toLowerCase()

    if (host.includes('instagram.com') || host.includes('instagr.am')) {
      return 'instagram'
    }
    if (
      host.includes('facebook.com') ||
      host.includes('fb.com') ||
      host.includes('fb.watch')
    ) {
      return 'facebook'
    }
    if (host.includes('tiktok.com')) {
      return 'tiktok'
    }
    if (host.includes('youtube.com') || host.includes('youtu.be')) {
      return 'youtube'
    }
    if (host.includes('linkedin.com')) {
      return 'linkedin'
    }
  } catch {
    // Invalid URL, return empty
  }
  return ''
}

const getPlatformIcon = (platform: string, className?: string) => {
  const iconClass = className || classes.platformIcon
  switch (platform) {
    case 'instagram':
      return <InstagramIcon className={iconClass} />
    case 'facebook':
      return <FacebookIcon className={iconClass} />
    case 'tiktok':
      return <TiktokIcon className={iconClass} />
    case 'youtube':
      return <YoutubeIcon className={iconClass} />
    case 'linkedin':
      return <LinkedinIcon className={iconClass} />
    default:
      return (
        <svg
          className={iconClass}
          fill="none"
          height="20"
          viewBox="0 0 24 24"
          width="20"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <path
            d="M2 12H22M12 2C14.5 4.5 16 8 16 12C16 16 14.5 19.5 12 22C9.5 19.5 8 16 8 12C8 8 9.5 4.5 12 2Z"
            stroke="currentColor"
            strokeWidth="1.5"
          />
        </svg>
      )
  }
}

export const AnalyzeRecentPostsModal: FC<AnalyzeRecentPostsModalProps> = ({
  initialData,
  isOpen,
  onClearAllLinks,
  onClose,
  onNext,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [analysisTitle, setAnalysisTitle] = useState<string>('')
  const [campaignCategory, setCampaignCategory] = useState<string>('')
  const [postCount, setPostCount] = useState<number>(0)
  const [urls, setUrls] = useState<UrlEntry[]>([
    { id: '1', link: '', platform: '' },
  ])
  const [isBulkPasteOpen, setIsBulkPasteOpen] = useState(false)
  const [bulkPasteText, setBulkPasteText] = useState('')
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null)
  const [dropdownPosition, setDropdownPosition] = useState<{
    left: number
    top: number
    width: number
  } | null>(null)

  // Initialize state from initialData when modal opens
  useEffect(() => {
    if (isOpen && initialData) {
      setAnalysisTitle(initialData.analysisTitle || '')
      setPostCount(initialData.postCount)
      setUrls(
        initialData.urls.length > 0
          ? initialData.urls
          : [{ id: '1', link: '', platform: '' }]
      )
    }
  }, [isOpen, initialData])

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      // Don't close if clicking inside modal or dropdown
      if (
        ref.current &&
        !ref.current.contains(target) &&
        !(target as Element).closest('[data-dropdown-menu]')
      ) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  useDisableScroll(isOpen)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClick = () => {
      setOpenDropdownId(null)
      setDropdownPosition(null)
    }

    if (openDropdownId) {
      document.addEventListener('click', handleClick)
    }

    return () => {
      document.removeEventListener('click', handleClick)
    }
  }, [openDropdownId])

  const toggleDropdown = (
    id: string,
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    event.stopPropagation()
    if (openDropdownId === id) {
      setOpenDropdownId(null)
      setDropdownPosition(null)
    } else {
      const rect = event.currentTarget.getBoundingClientRect()
      setDropdownPosition({
        left: rect.left,
        top: rect.bottom,
        width: rect.width,
      })
      setOpenDropdownId(id)
    }
  }

  const handleUrlChange = (
    id: string,
    field: 'platform' | 'link',
    value: string
  ) => {
    if (field === 'link') {
      const detectedPlatform = detectPlatformFromUrl(value)
      setUrls(
        urls.map((url) =>
          url.id === id
            ? {
                ...url,
                link: value,
                platform: detectedPlatform || url.platform,
              }
            : url
        )
      )
    } else {
      setUrls(
        urls.map((url) => (url.id === id ? { ...url, [field]: value } : url))
      )
    }
  }

  const selectPlatform = (id: string, platform: string) => {
    handleUrlChange(id, 'platform', platform)
    setOpenDropdownId(null)
    setDropdownPosition(null)
  }

  const handleAddLink = () => {
    setUrls([...urls, { id: Date.now().toString(), link: '', platform: '' }])
  }

  const handleBulkPaste = () => {
    const lines = bulkPasteText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0)

    if (lines.length === 0) return

    const newUrls = lines.map((line) => ({
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      link: line,
      platform: detectPlatformFromUrl(line),
    }))

    setUrls([...urls.filter((u) => u.link !== ''), ...newUrls])
    setBulkPasteText('')
    setIsBulkPasteOpen(false)
  }

  const handleNext = () => {
    if (onNext) {
      onNext({ analysisTitle, category: campaignCategory, postCount, urls })
    }
  }

  // Check if form is valid
  const isFormValid =
    analysisTitle.trim() !== '' &&
    postCount > 0 &&
    urls.some((url) => url.link.trim() !== '' && url.platform !== '')

  if (!isOpen) return null

  return (
    <div className={classes.modalOverlay}>
      <div className={classes.modalContent} ref={ref}>
        <div className={classes.modalHeader}>
          <h2 className={classes.modalTitle}>Analyze Recent Posts</h2>
          <button
            className={classes.closeButton}
            onClick={onClose}
            type="button"
          >
            <svg fill="none" height="24" viewBox="0 0 24 24" width="24">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
              />
            </svg>
          </button>
        </div>

        <div className={classes.modalBody}>
          <div className={classes.inputWrapper}>
            <label className={classes.floatingLabel}>
              Analysis Title <span className={classes.required}>*</span>
            </label>
            <input
              className={classes.textInput}
              onChange={(e) => setAnalysisTitle(e.target.value)}
              placeholder="example Hapttic123"
              type="text"
              value={analysisTitle}
            />
          </div>

          <CampaignCategoryPicker
            exclude={['Review Analysis']}
            onChange={setCampaignCategory}
            value={campaignCategory}
          />

          <div className={classes.inputWrapper}>
            <label className={classes.floatingLabel}>
              Number of recent posts <span className={classes.required}>*</span>
            </label>
            <input
              className={classes.numberInput}
              min={0}
              onChange={(e) => setPostCount(Number(e.target.value))}
              placeholder="0"
              type="number"
              value={postCount === 0 ? '' : postCount}
            />
          </div>

          <div className={classes.urlsSection}>
            <div className={classes.urlsHeader}>
              <span className={classes.urlsLabel}>URLs:</span>
              <button
                className={classes.bulkPasteButton}
                onClick={() => setIsBulkPasteOpen(true)}
                type="button"
              >
                <svg fill="none" height="16" viewBox="0 0 24 24" width="16">
                  <path
                    d="M4 6H20M4 12H20M4 18H20"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeWidth="2"
                  />
                </svg>
                Bulk Paste
              </button>
            </div>

            {isBulkPasteOpen ? (
              <div className={classes.bulkPasteContainer}>
                <textarea
                  className={classes.bulkPasteTextarea}
                  onChange={(e) => setBulkPasteText(e.target.value)}
                  placeholder={`Paste multiple URLs here, one per line:\nhttps://facebook.com...\nhttps://instagram.com...\nhttps://tiktok.com...\nhttps://youtube.com...\nhttps://linkedin.com...`}
                  value={bulkPasteText}
                />
                <div className={classes.bulkPasteFooter}>
                  <button
                    className={classes.cancelButton}
                    onClick={() => {
                      setIsBulkPasteOpen(false)
                      setBulkPasteText('')
                    }}
                    type="button"
                  >
                    Cancel
                  </button>
                  <button
                    className={`${classes.addLinksButton} ${
                      !bulkPasteText.trim()
                        ? classes.addLinksButtonDisabled
                        : ''
                    }`}
                    disabled={!bulkPasteText.trim()}
                    onClick={handleBulkPaste}
                    type="button"
                  >
                    Add Links
                  </button>
                </div>
              </div>
            ) : (
              <div className={classes.urlsTable}>
                <div className={classes.urlsTableScroll}>
                  {urls.map((url) => (
                    <div className={classes.urlRow} key={url.id}>
                      <div className={classes.platformCell}>
                        <label className={classes.cellLabel}>Platform</label>
                        <div
                          className={classes.selectWrapper}
                          onClick={(e) => toggleDropdown(url.id, e)}
                        >
                          {getPlatformIcon(
                            url.platform,
                            classes.platformIconInline
                          )}
                          <span className={classes.selectText}>
                            {url.platform
                              ? url.platform.charAt(0).toUpperCase() +
                                url.platform.slice(1)
                              : 'Select'}
                          </span>
                          <svg
                            className={classes.chevronIcon}
                            fill="none"
                            height="16"
                            viewBox="0 0 24 24"
                            width="16"
                          >
                            <path
                              d="M6 9L12 15L18 9"
                              stroke="currentColor"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                            />
                          </svg>
                        </div>
                        {openDropdownId === url.id &&
                          dropdownPosition &&
                          createPortal(
                            <div
                              className={classes.dropdownMenu}
                              data-dropdown-menu
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                left: `${dropdownPosition.left}px`,
                                position: 'fixed',
                                top: `${dropdownPosition.top}px`,
                              }}
                            >
                              <div
                                className={classes.dropdownItem}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  selectPlatform(url.id, 'instagram')
                                }}
                              >
                                <InstagramIcon
                                  className={classes.dropdownIcon}
                                />
                                Instagram
                              </div>
                              <div
                                className={classes.dropdownItem}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  selectPlatform(url.id, 'facebook')
                                }}
                              >
                                <FacebookIcon
                                  className={classes.dropdownIcon}
                                />
                                Facebook
                              </div>
                              <div
                                className={classes.dropdownItem}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  selectPlatform(url.id, 'tiktok')
                                }}
                              >
                                <TiktokIcon className={classes.dropdownIcon} />
                                TikTok
                              </div>
                              <div
                                className={classes.dropdownItem}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  selectPlatform(url.id, 'youtube')
                                }}
                              >
                                <YoutubeIcon className={classes.dropdownIcon} />
                                Youtube
                              </div>
                              <div
                                className={classes.dropdownItem}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  selectPlatform(url.id, 'linkedin')
                                }}
                              >
                                <LinkedinIcon
                                  className={classes.dropdownIcon}
                                />
                                LinkedIn
                              </div>
                            </div>,
                            document.body
                          )}
                      </div>
                      <div className={classes.linkCell}>
                        <label className={classes.cellLabel}>Page Link</label>
                        <input
                          className={classes.linkInput}
                          onChange={(e) =>
                            handleUrlChange(url.id, 'link', e.target.value)
                          }
                          placeholder="Paste here"
                          type="text"
                          value={url.link}
                        />
                      </div>
                      {urls.length > 1 && (
                        <button
                          className={classes.deleteButton}
                          onClick={() =>
                            setUrls(urls.filter((u) => u.id !== url.id))
                          }
                          type="button"
                        >
                          <DeleteIcon />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  className={classes.addLinkButton}
                  onClick={handleAddLink}
                  type="button"
                >
                  <svg fill="none" height="16" viewBox="0 0 24 24" width="16">
                    <path
                      d="M12 5V19M5 12H19"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeWidth="2"
                    />
                  </svg>
                  Add More Links
                  <span className={classes.shortcut}>⌘↵</span>
                </button>
              </div>
            )}
          </div>
        </div>

        <div className={classes.modalFooter}>
          {urls.length > 1 && (
            <button
              className={classes.clearAllButton}
              onClick={() => {
                if (onClearAllLinks) {
                  onClearAllLinks()
                }
              }}
              type="button"
            >
              Clear All Links
            </button>
          )}
          <div className={classes.footerRight}>
            <button
              className={classes.cancelButton}
              onClick={onClose}
              type="button"
            >
              Cancel
            </button>
            <button
              className={classes.nextButton}
              disabled={!isFormValid}
              onClick={handleNext}
              type="button"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
